import { useContext, type KeyboardEventHandler } from 'react';
import { ActiveTabContext } from './TabProvider';

const LEFT = 'ArrowLeft';
const RIGHT = 'ArrowRight';

function getIndex(length: number, step: number, i: number): number {
    // use modulo to traverse array circularly. bc modulo can be negative in JS, it requires
    // addition to ensure the result is always positive
    return (((i + step) % length) + length) % length;
}

export const useTabList = (
    tabNames: string[], // ordered list of tabNames
    onTabActivate: (name: string) => void = () => {},
    automaticActivation?: boolean
): { role: 'tablist'; onKeyDown: KeyboardEventHandler } => {
    const {
        setActiveTab = () => {},
        selectedTab,
        setSelectedTab = () => {},
    } = useContext(ActiveTabContext) || {};

    // allow falsy in children, but remove them to allow for conditional rendering
    // in consumers.
    // e.g. children = [isAllowed ? <MyTab> : null, flag && <MyTab/>, <MyTab />]

    const handleKeyDown: KeyboardEventHandler = function (e) {
        if (e.key === LEFT || e.key === RIGHT) {
            e.preventDefault();
            const step = e.key === LEFT ? -1 : 1;
            const currentIndex = tabNames.findIndex(name => name === selectedTab);
            const newIndex = getIndex(tabNames.length, step, currentIndex);
            const activeTabName = tabNames[newIndex];
            setSelectedTab(activeTabName);
            if (automaticActivation) {
                setActiveTab(activeTabName);
                onTabActivate(activeTabName);
            }
        }
    };
    return { role: 'tablist', onKeyDown: handleKeyDown };
};
